import { mainApi } from '../../../api/mainApi';
import { AccessState } from '../../../types';
import { middlewareApi } from '../../middleware/middlewareApi';
import { PersonPayload } from '../interfaces/person.interfaces';

import { SearchPersonParamResponse } from '../interfaces/searchPerson.interface';

interface UploadPhotoResponse {
  name: string;
  route: string;
  url: string;
}

export const searchPersonTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['SearchPersons'],
});
// Define a service using a base URL and expected endpoints
export const searchPersonApi = searchPersonTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET get persons
     * @Permission persons-obtener-person-by-params
     */
    getPersonsByParam: builder.query<
      SearchPersonParamResponse,
      {
        profileId?: number;
        page?: number;
        perPage?: number;
        ci?: string;
        firstname?: string;
        secondname?: string;
        first_lastname?: string;
        second_lastname?: string;
        is_student?: boolean;
        is_teacher?: boolean;
        is_administrative?: boolean;
        is_external?: boolean;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          perPage = 10,
          ci,
          firstname,
          secondname,
          first_lastname,
          second_lastname,
          is_student,
          is_teacher,
          is_administrative,
          is_external,
        },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<SearchPersonParamResponse>(
            `/persons/search-person/params`,
            {
              params: {
                ...(profileId ? { user_profile_id: profileId } : { user_profile_id }),
                ...(page && { page }),
                ...(perPage && { size: perPage }),
                ...(ci && { search: ci }),
                ...(firstname && { firstname }),
                ...(secondname && { secondname }),
                ...(first_lastname && { first_lastname }),
                ...(second_lastname && { second_lastname }),
                ...(is_student && { is_student: 1 }),
                ...(is_teacher && { is_teacher: 1 }),
                ...(is_administrative && { is_administrative: 1 }),
                ...(is_external && { is_external: 1 }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'SearchPersons', id } as const)),
              { type: 'SearchPersons', id: 'LIST' },
            ]
          : [{ type: 'SearchPersons', id: 'LIST' }],
    }),

    /**
     * @GET get persons by mutation
     * @Permission persons-obtener-person-by-params
     */
    getPersonsByParamNoCache: builder.mutation<
      SearchPersonParamResponse,
      {
        profileId: number;
        page?: number;
        ci?: string;
        firstname?: string;
        secondname?: string;
        first_lastname?: string;
        second_lastname?: string;
        is_student?: boolean;
        is_teacher?: boolean;
        is_administrative?: boolean;
      }
    >({
      queryFn: async ({
        profileId,
        page,
        ci,
        firstname,
        secondname,
        first_lastname,
        second_lastname,
        is_student,
        is_teacher,
        is_administrative,
      }) => {
        try {
          const { data } = await mainApi.get<SearchPersonParamResponse>(
            `/persons/search-person/params`,
            {
              params: {
                user_profile_id: profileId,
                ...(page && { page }),
                ...(ci && { search: ci }),
                ...(firstname && { firstname }),
                ...(secondname && { secondname }),
                ...(first_lastname && { first_lastname }),
                ...(second_lastname && { second_lastname }),
                ...(is_student && { is_student: 1 }),
                ...(is_teacher && { is_teacher: 1 }),
                ...(is_administrative && { is_administrative: 1 }),
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    // Create person

    /**
     * @GET create person mutation
     */
    createPersonData: builder.mutation<
      PersonPayload,
      { userProfileId: number; payload: PersonPayload }
    >({
      queryFn: async ({ payload, userProfileId }) => {
        try {
          const { data } = await mainApi.post<PersonPayload>(`/persons`, {
            user_profile_id: userProfileId,
            ...payload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    addPhotoPerson: builder.mutation<string, { userProfileId: number; file: File }>({
      queryFn: async ({ userProfileId, file }) => {
        try {
          const form = new FormData();
          form.append('files[]', file);
          form.append('type_document', '13');
          form.append('user_profile_id', userProfileId.toString());

          const { data } = await mainApi.post<UploadPhotoResponse[]>(
            '/users/profiles/photos',
            form,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );

          const fileName = data[0]?.route!;

          return { data: fileName };
        } catch (error: any) {
          return { error };
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPersonsByParamQuery,
  useLazyGetPersonsByParamQuery,
  useCreatePersonDataMutation,
  useAddPhotoPersonMutation,
  useGetPersonsByParamNoCacheMutation,
} = searchPersonApi;
